/**
 * Plugin Command Palette Registry
 *
 * Frontend registry for plugin-provided commands.
 * Works in conjunction with backend PluginCommandPaletteRegistryService.
 * Part of Phase 0: Core Preparation for Modularization.
 */

import { create } from 'zustand';

/**
 * Command argument type
 */
export type CommandArgumentType = 'string' | 'number' | 'boolean' | 'select' | 'entity' | 'file';

/**
 * Command argument definition
 */
export interface CommandArgument {
  /** Argument name */
  name: string;
  /** Argument type */
  type: CommandArgumentType;
  /** Human-readable label */
  label: string;
  /** Description */
  description?: string;
  /** Whether argument is required */
  required?: boolean;
  /** Default value */
  default?: any;
  /** Options for select type */
  options?: Array<{ label: string; value: any }>;
  /** Entity type for entity type */
  entityType?: string;
}

/**
 * Command definition
 */
export interface CommandDefinition {
  /** Unique command ID */
  id: string;
  /** Plugin that owns this command */
  pluginId: string;
  /** Human-readable name */
  name: string;
  /** Description */
  description?: string;
  /** Icon */
  icon?: string;
  /** Category for grouping */
  category: string;
  /** Keywords for search */
  keywords: string[];
  /** Command arguments */
  args?: CommandArgument[];
  /** Keyboard shortcut */
  shortcut?: string;
  /** Whether command is enabled */
  enabled: boolean;
}

/**
 * Command search result
 */
export interface CommandSearchResult {
  command: CommandDefinition;
  score: number;
  highlights: string[];
}

/**
 * Command execution context
 */
export interface CommandExecutionContext {
  entityType?: string;
  entityId?: string;
  selection?: any;
  [key: string]: any;
}

/**
 * Command handler function
 */
export type CommandHandler = (args: Record<string, any>, context: CommandExecutionContext) => Promise<any>;

/**
 * Command registry state
 */
interface CommandRegistryState {
  /** Map of command ID to definition */
  commands: Map<string, CommandDefinition>;
  /** Map of category to command IDs */
  commandsByCategory: Map<string, Set<string>>;
  /** Map of command ID to handler */
  handlers: Map<string, CommandHandler>;
  /** Map of shortcut to command ID */
  shortcuts: Map<string, string>;
  /** Whether palette is open */
  isOpen: boolean;
  /** Current search query */
  query: string;
  /** Search results */
  results: CommandSearchResult[];
  
  // Actions
  registerCommand: (command: CommandDefinition, handler?: CommandHandler) => void;
  unregisterCommand: (commandId: string) => void;
  unregisterPluginCommands: (pluginId: string) => void;
  executeCommand: (commandId: string, args: Record<string, any>, context: CommandExecutionContext) => Promise<any>;
  executeByShortcut: (shortcut: string, context: CommandExecutionContext) => Promise<any>;
  search: (query: string, context?: CommandExecutionContext) => void;
  openPalette: () => void;
  closePalette: () => void;
  togglePalette: () => void;
  setQuery: (query: string) => void;
  getCommand: (commandId: string) => CommandDefinition | undefined;
  getAllCommands: () => CommandDefinition[];
  getCommandsByCategory: () => Map<string, CommandDefinition[]>;
  syncFromBackend: (commands: CommandDefinition[]) => void;
}

/**
 * Search commands
 */
function searchCommands(
  commands: Map<string, CommandDefinition>,
  query: string,
  _context?: CommandExecutionContext,
): CommandSearchResult[] {
  const results: CommandSearchResult[] = [];
  const queryLower = query.toLowerCase();
  const queryWords = queryLower.split(/\s+/);

  for (const [, command] of commands) {
    if (!command.enabled) continue;

    let score = 0;
    const highlights: string[] = [];

    // Match against name
    const nameLower = command.name.toLowerCase();
    if (nameLower.includes(queryLower)) {
      score += 100;
      highlights.push(command.name);
    } else {
      for (const word of queryWords) {
        if (nameLower.includes(word)) {
          score += 50;
          highlights.push(command.name);
        }
      }
    }

    // Match against description
    if (command.description) {
      const descLower = command.description.toLowerCase();
      if (descLower.includes(queryLower)) {
        score += 30;
        highlights.push(command.description);
      }
    }

    // Match against keywords
    for (const keyword of command.keywords) {
      if (keyword.toLowerCase().includes(queryLower)) {
        score += 40;
        highlights.push(keyword);
      }
    }

    // Match against category
    if (command.category.toLowerCase().includes(queryLower)) {
      score += 20;
    }

    if (score > 0) {
      results.push({ command, score, highlights });
    }
  }

  // Sort by score
  results.sort((a, b) => b.score - a.score);

  return results;
}

/**
 * Command registry store
 */
export const useCommandRegistry = create<CommandRegistryState>((set, get) => ({
  commands: new Map(),
  commandsByCategory: new Map(),
  handlers: new Map(),
  shortcuts: new Map(),
  isOpen: false,
  query: '',
  results: [],

  registerCommand: (command: CommandDefinition, handler?: CommandHandler) => {
    set((state) => {
      const newCommands = new Map(state.commands);
      const newByCategory = new Map(state.commandsByCategory);
      const newHandlers = new Map(state.handlers);
      const newShortcuts = new Map(state.shortcuts);
      
      // Remove from old category if updating
      const existing = newCommands.get(command.id);
      if (existing) {
        newByCategory.get(existing.category)?.delete(command.id);
        if (existing.shortcut) {
          newShortcuts.delete(existing.shortcut);
        }
      }
      
      newCommands.set(command.id, command);
      
      if (!newByCategory.has(command.category)) {
        newByCategory.set(command.category, new Set());
      }
      newByCategory.get(command.category)!.add(command.id);
      
      if (handler) {
        newHandlers.set(command.id, handler);
      }
      
      if (command.shortcut) {
        newShortcuts.set(command.shortcut, command.id);
      }
      
      return { 
        commands: newCommands, 
        commandsByCategory: newByCategory,
        handlers: newHandlers,
        shortcuts: newShortcuts,
      };
    });
  },

  unregisterCommand: (commandId: string) => {
    set((state) => {
      const command = state.commands.get(commandId);
      if (!command) return state;
      
      const newCommands = new Map(state.commands);
      const newByCategory = new Map(state.commandsByCategory);
      const newHandlers = new Map(state.handlers);
      const newShortcuts = new Map(state.shortcuts);
      
      newCommands.delete(commandId);
      newByCategory.get(command.category)?.delete(commandId);
      newHandlers.delete(commandId);
      
      if (command.shortcut) {
        newShortcuts.delete(command.shortcut);
      }
      
      return { 
        commands: newCommands, 
        commandsByCategory: newByCategory,
        handlers: newHandlers,
        shortcuts: newShortcuts,
      };
    });
  },

  unregisterPluginCommands: (pluginId: string) => {
    set((state) => {
      const newCommands = new Map(state.commands);
      const newByCategory = new Map(state.commandsByCategory);
      const newHandlers = new Map(state.handlers);
      const newShortcuts = new Map(state.shortcuts);
      
      for (const [id, command] of newCommands) {
        if (command.pluginId === pluginId) {
          newCommands.delete(id);
          newByCategory.get(command.category)?.delete(id);
          newHandlers.delete(id);
          if (command.shortcut) {
            newShortcuts.delete(command.shortcut);
          }
        }
      }
      
      return { 
        commands: newCommands, 
        commandsByCategory: newByCategory,
        handlers: newHandlers,
        shortcuts: newShortcuts,
      };
    });
  },

  executeCommand: async (commandId: string, args: Record<string, any>, context: CommandExecutionContext) => {
    const state = get();
    const command = state.commands.get(commandId);
    
    if (!command) {
      throw new Error(`Command ${commandId} not found`);
    }
    
    if (!command.enabled) {
      throw new Error(`Command ${commandId} is disabled`);
    }
    
    const handler = state.handlers.get(commandId);
    
    // If we have a local handler, use it
    if (handler) {
      return await handler(args, context);
    }
    
    // Otherwise, call backend to execute
    const response = await fetch('/api/plugins/commands/execute', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ commandId, args, context }),
    });
    
    if (!response.ok) {
      throw new Error(`Failed to execute command: ${response.statusText}`);
    }
    
    const result = await response.json();
    return result;
  },

  executeByShortcut: async (shortcut: string, context: CommandExecutionContext) => {
    const commandId = get().shortcuts.get(shortcut);
    if (!commandId) {
      throw new Error(`No command bound to shortcut ${shortcut}`);
    }
    return get().executeCommand(commandId, {}, context);
  },

  search: (query: string, context?: CommandExecutionContext) => {
    const results = searchCommands(get().commands, query, context);
    set({ query, results });
  },

  openPalette: () => {
    set({ isOpen: true, query: '', results: [] });
  },

  closePalette: () => {
    set({ isOpen: false, query: '', results: [] });
  },

  togglePalette: () => {
    const isOpen = get().isOpen;
    if (isOpen) {
      get().closePalette();
    } else {
      get().openPalette();
    }
  },

  setQuery: (query: string) => {
    get().search(query);
  },

  getCommand: (commandId: string) => {
    return get().commands.get(commandId);
  },

  getAllCommands: () => {
    return Array.from(get().commands.values()).filter(c => c.enabled);
  },

  getCommandsByCategory: () => {
    const state = get();
    const result = new Map<string, CommandDefinition[]>();
    
    for (const [category, commandIds] of state.commandsByCategory) {
      const commands = Array.from(commandIds)
        .map(id => state.commands.get(id))
        .filter((c): c is CommandDefinition => c !== undefined && c.enabled);
      
      if (commands.length > 0) {
        result.set(category, commands);
      }
    }
    
    return result;
  },

  syncFromBackend: (commands: CommandDefinition[]) => {
    set(() => {
      const newCommands = new Map<string, CommandDefinition>();
      const newByCategory = new Map<string, Set<string>>();
      const newShortcuts = new Map<string, string>();
      
      for (const command of commands) {
        newCommands.set(command.id, command);
        
        if (!newByCategory.has(command.category)) {
          newByCategory.set(command.category, new Set());
        }
        newByCategory.get(command.category)!.add(command.id);
        
        if (command.shortcut) {
          newShortcuts.set(command.shortcut, command.id);
        }
      }
      
      return { 
        commands: newCommands, 
        commandsByCategory: newByCategory,
        shortcuts: newShortcuts,
      };
    });
  },
}));

/**
 * Hook to use command palette state
 */
export function useCommandPalette() {
  return useCommandRegistry((state) => ({
    isOpen: state.isOpen,
    query: state.query,
    results: state.results,
    openPalette: state.openPalette,
    closePalette: state.closePalette,
    togglePalette: state.togglePalette,
    setQuery: state.setQuery,
    executeCommand: state.executeCommand,
  }));
}
